/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    static mseNormalizeSearchTerm(value) {
      const text = String(value || '').trim();
      const zeroLetter = /^#?[oO](\d{2})$/.exec(text);
      if (zeroLetter) return '0' + zeroLetter[1];
      const extraZero = /^#?00(\d{2})$/.exec(text);
      if (extraZero) return '0' + extraZero[1];
      const number = /^#(\d{3,4})$/.exec(text);
      if (number) return number[1];
      const correction = {
        acril: 'acryl',
        acrilpulver: 'acrylpulver',
        nagelfiele: 'nagelfeile',
        naglfeile: 'nagelfeile',
        buferr: 'buffer',
        bufferr: 'buffer',
        polierfiele: 'polierfeile',
        liqiud: 'liquid',
        modelierfluessigkeit: 'modellierfluessigkeit',
        gelaxi: 'gelaxy',
        shelac: 'shellac'
      };
      return text.replace(/[A-Za-z]+/g, word => correction[word.toLowerCase()] || word);
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (
        !this.getQuery().length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      );
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      this.setLiveRegionLoadingState();
      searchTerm = PredictiveSearch.mseNormalizeSearchTerm(searchTerm);

      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      let searchParams = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(searchTerm)}`;
      } else {
        searchParams = `q=${encodeURIComponent(searchTerm)}`;
      }

      searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${
        theme.settings.pSearchLimitScope
      }`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      searchParams += `&${encodeURIComponent('resources[options][unavailable_products]')}=last`;
      searchParams += '&section_id=predictive-search';

      const queryKey = searchParams.replace(' ', '-').toLowerCase();
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`);
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        // MSE_SEARCH_COLOR_NUMBER_PRIORITY_V1_20261008
        // Exact colour numbers first, then Gelaxy, then other matching products.
        const numberedSearch = /^#?(\d{3,4})$/.exec(searchTerm);
        if (numberedSearch && resultsEl) {
          const colourNumber = numberedSearch[1];
          const productList = resultsEl.querySelector('#psearch-products .predictive-search__list');
          if (productList) {
            const exactNumber = new RegExp('(^|[^0-9])' + colourNumber + '(?![0-9])');
            const productCards = [...productList.children].filter(
              item => item.classList.contains('predictive-search__item') && !item.classList.contains('js-submit')
            );
            const rank = item => {
              const title = item.querySelector('.predictive-result__title')?.textContent || '';
              if (!exactNumber.test(title)) return 3;
              if (/gelaxy|shellac/i.test(title)) return 1;
              if (/farbgel|color gel/i.test(title)) return 0;
              return 2;
            };
            const orderedCards = productCards.filter(item => {
              if (colourNumber.length === 3 && colourNumber.startsWith('0') && rank(item) === 3) {
                item.remove();
                return false;
              }
              return true;
            });
            orderedCards.sort((a, b) => {
              const availability = Number(a.dataset.mseAvailable === 'false')
                - Number(b.dataset.mseAvailable === 'false');
              return availability || rank(a) - rank(b);
            });
            const viewAll = productList.querySelector('.js-submit');
            orderedCards.forEach(item => productList.insertBefore(item, viewAll));
          }
        }
        const mseOosCatalog = {"114":{"handle":"114-premium-pure-color-gel-5ml-mittleres-braun","title":"Premium-PURE Color Gel 5ml Zartbitterschokolade"},"274":{"handle":"274-premium-glitter-color-gel-5ml-blasses-orange-mit-feinem-orange-irisierendem-glitter","title":"Premium-GLITTER Color Gel 5ml Blasses Orange mit feinem orange-irisierendem Glitter"},"362":{"handle":"362-premium-pure-color-gel-5ml-meeresblaugrun","title":"Premium-PURE Color Gel 5ml Meeresblaugrün"},"385":{"handle":"385-premium-pure-color-gel-5ml-graulicher-fliederton","title":"Premium-PURE Color Gel 5ml Gräulicher Fliederton"},"432":{"handle":"432-premium-glitter-color-gel-5ml-gel-mit-pinkfarbenen-und-dunkellila-glitter","title":"Premium-GLITTER Color Gel 5ml Gel mit pinkfarbenen und dunkellila Glitter"},"460":{"handle":"460-premium-pure-color-gel-5ml-intensives-hellblau","title":"Premium-PURE Color Gel 5ml Intensives Hellblau"},"462":{"handle":"462-premium-pure-color-gel-5ml-blasses-rauchblau","title":"Premium-PURE Color Gel 5ml Blasses Rauchblau"},"658":{"handle":"658-premium-pure-color-gel-5ml-blau","title":"Premium-PURE Color Gel 5ml Blau"},"670":{"handle":"670-premium-deko-color-gel-5ml-deko","title":"Premium-DEKO Color Gel 5ml Deko"},"676":{"handle":"676-premium-effekt-color-gel-5ml-blau","title":"Premium-EFFEKT Color Gel 5ml Blau"},"714":{"handle":"714-premium-effekt-color-gel-5ml-beige","title":"Premium-EFFEKT Color Gel 5ml Beige"},"730":{"handle":"730-premium-glitter-color-gel-5ml-rosa","title":"Premium-GLITTER Color Gel 5ml Rosa"},"754":{"handle":"754-premium-pure-color-gel-5ml-dunkelrot","title":"Premium-PURE Color Gel 5ml Dunkelrot"},"796":{"handle":"796-premium-pure-color-gel-5ml-pink","title":"Premium-PURE Color Gel 5ml Pink"},"842":{"handle":"842-premium-deko-color-gel-5ml-deko","title":"Premium-DEKO Color Gel 5ml Deko"},"845":{"handle":"845-premium-glitter-color-gel-5ml-rosa","title":"Premium-GLITTER Color Gel 5ml Rosa"},"846":{"handle":"846-premium-pure-color-gel-5ml-blau","title":"Premium-PURE Color Gel 5ml Blau"},"855":{"handle":"855-premium-pure-color-gel-5ml-orange","title":"Premium-PURE Color Gel 5ml Orange"},"889":{"handle":"889-premium-pure-color-gel-5ml-blau","title":"Premium-PURE Color Gel 5ml Blau"},"1063":{"handle":"1063-pure-farbgel-5ml-blau","title":"PURE Farbgel 5ml Blau"}};
        const mseNumber = searchTerm.replace(/^#/, '')
          .replace(/^1505(\d{3})$/, '$1')
          .replace(/^150(1\d{3})$/, '$1');
        const mseRecord = Object.prototype.hasOwnProperty.call(mseOosCatalog, mseNumber)
          ? mseOosCatalog[mseNumber] : null;
        if (mseRecord && resultsEl) {
          let stillUnpublished = false;
          try {
            const response = await fetch('/products/' + encodeURIComponent(mseRecord.handle) + '.js',
              { method: 'GET', credentials: 'same-origin' });
            stillUnpublished = response.status === 404;
          } catch (_error) {
            stillUnpublished = false;
          }
          if (stillUnpublished) {
            const products = resultsEl.querySelector('#psearch-products .predictive-search__list');
            const row = document.createElement(products ? 'li' : 'div');
            row.className = products ? 'predictive-search__item mse-out-of-sales-result' : 'mse-out-of-sales-result';
            row.style.padding = '14px';
            row.style.borderTop = '1px solid #d9d2eb';
            const label = document.createElement('strong');
            label.style.display = 'block';
            label.textContent = '#' + mseNumber + ' – ' + mseRecord.title + ' – OUT OF SALES';
            const note = document.createElement('p');
            note.style.fontSize = '13px';
            note.textContent = 'Derzeit nicht bestellbar.';
            const link = document.createElement('a');
            link.href = '/search?type=product&q=' + encodeURIComponent(mseNumber);
            link.textContent = 'Informiere mich, sollte er wieder zum Verkauf stehen';
            link.className = 'link';
            row.append(label, note, link);
            if (products) products.insertBefore(row, products.querySelector('.js-submit'));
            else {
              const empty = resultsEl.querySelector('.predictive-search__no-results');
              if (empty) empty.replaceWith(row);
              else resultsEl.querySelector('.predictive-search__results')?.append(row);
            }
          }
        }
        const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup);
      } catch (error) {
        this.close();
        throw error;
      }
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.results.innerHTML = resultsMarkup;
      this.setAttribute('results', true);
      this.open();
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
